import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Heart, Loader2, MoveLeft, MoveRight, Search } from "lucide-react";
import InfiniteScroll from "react-infinite-scroll-component";
import { createSelector } from "@reduxjs/toolkit";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import {
  setIsEditing,
  setSelectedProducts,
  resetSelectedProducts,
  setCurrentPage,
  setDiscountInfo,
  updateDiscountInfo,
  setIsLoading,
} from "slices/discount/reducer";
import { Link } from "react-router-dom";
import * as Yup from "yup";
import {
  getJewelleryDiscountModel as onGetJewelleryDiscountModel,
  addJewelleryDiscount as onAddJewelleryDiscount,
  getJewelleryDiscount as onGetJewelleryDiscount,
  updateJewelleryDiscount as onUpdateJewelleryDiscount,
} from "slices/thunk";
import { debounce } from "utils/debounce";
import { useFormik } from "formik";
import { useTabContext } from "context/TabContext";

interface FilterOptions {
  search?: string;
  gender?: "male" | "female";
  category?: string;
  subcategory?: string;
  shape?: string;
  onlyDiscounted?: boolean;
}

interface DiscountProductFormProps {
  eventData: any;
  // selectedProducts: any;
  isEdit: boolean;
  toggle: () => void;
  discounts: any;
  showAllProducts: boolean;
  setShowAllProducts: React.Dispatch<React.SetStateAction<boolean>>;
}

const DiscountJewelleryProduct: React.FC<DiscountProductFormProps> = ({
  eventData,
  isEdit,
  toggle,
  discounts,
  showAllProducts,
  setShowAllProducts,
}) => {
  const dispatch = useDispatch<any>();
  const { changeTab } = useTabContext();

  const selectDataList = createSelector(
    (state: any) => state.Discount,
    (state) => ({
      jewelleries: state.jewelleryDiscountModel,
      selectedProducts: state.selectedProducts || [],
      totalCount: state.totalCount,
      currentPage: state.currentPage,
      discountInfo: state.discountInfo,
      isLoading: state.isLoading,
    })
  );

  const {
    jewelleries,
    selectedProducts,
    currentPage,
    totalCount,
    discountInfo,
    isLoading,
  } = useSelector(selectDataList);

  const [filterOptions, setFilterOptions] = useState<FilterOptions>({});
  // const [showAllProducts, setShowAllProducts] = useState<boolean>(false);
  const [showUpdateButtons, setShowUpdateButtons] = useState<boolean>(false);
  const [isFormChanged, setIsFormChanged] = useState<boolean>(false);

  const selectedIds = selectedProducts.map((products: any) => products?._id);
  const isButtonEnabled =
    discountInfo.type !== eventData?.type ||
    discountInfo.value !== eventData?.value ||
    discountInfo.startDate !== eventData?.startDate ||
    discountInfo.endDate !== eventData?.endDate ||
    JSON.stringify(eventData?.products) !== JSON.stringify(selectedProducts);

  const validation: any = useFormik({
    // enableReinitialize : use this flag when initial values needs to be changed
    enableReinitialize: true,
    initialValues: {
      products: selectedIds,
    },
    validationSchema: Yup.object({
      products: Yup.array().of(Yup.string()).default([]),
    }),

    onSubmit: async (values) => {
      const productsWithFlags = values.products.map((id: string) => {
        const isSelected = selectedProducts.some(
          (prod: any) => prod._id === id
        );
        return {
          id,
          isNew: isSelected, // Mark as new if not in selectedProducts
          isDeleted: !isSelected, // Mark as deleted if it was previously selected
        };
      });

      dispatch(setIsLoading(true));
      // save new Discount
      if (isEdit) {
        const updateData = {
          id: eventData._id,
          fields: {
            ...discountInfo,
            products: productsWithFlags,
          },
        };
        try {
          const result = await dispatch(onUpdateJewelleryDiscount(updateData));
          if (result.status === 200) {
            setShowAllProducts(false);
            changeTab("completed");
          }
        } catch (error) {
          console.error("Error updating jewellery discount:", error);
          // Handle the error as needed
        }
      } else {
        const payload = {
          ...discountInfo,
          products: selectedIds,
        };
        try {
          const result = dispatch(onAddJewelleryDiscount(payload));

          if (result.status === 200) {
            // Change the tab only after loading is done and success is true
            changeTab("completed");
          }
        } catch (error) {
          console.error("Error adding jewellery discount:", error);
          // Handle the error as needed
        }
      }
    },
  });

  const handleProductClick = (product: any) => {
    const isSelected = selectedProducts.some(
      (prod: any) => prod._id === product._id
    );

    if (showUpdateButtons || !isEdit) {
      const updatedProducts = isSelected
        ? selectedProducts.filter((prod: any) => prod?._id !== product?._id) // Remove the product if it's already selected
        : Array.from(new Set([...selectedProducts, product])); // Add the product and include eventProductIds

      validation.setFieldValue("products", updatedProducts);
      dispatch(setSelectedProducts(updatedProducts));
    }
  };

  useEffect(() => {
    // If the form is dirty, show the update buttons
    setIsFormChanged(!validation.dirty);
    // if (validation.dirty) {
    //   setShowUpdateButtons(true);
    // }
  }, [validation.dirty]);

  const loadMoreJewelleries = async (page: number) => {
    try {
      const payload = {
        filterOptions,
        page,
        pageSize: 10,
      };
      dispatch(onGetJewelleryDiscountModel(payload));
      dispatch(setCurrentPage(page));
    } catch (error) {
      console.error("Error loading more jewelleries:", error);
    }
  };

  const toggleEditMode = () => {
    setShowUpdateButtons(true);
    validation.resetForm(); // Reset the form when toggling to edit mode
    // dispatch(resetSelectedProducts()); // Reset the selected products when editing
    setIsFormChanged(false);
  };

  useEffect(() => {
    if (filterOptions.search) {
      debouncedSearch(); // Trigger the debounced function
    }
  }, [filterOptions.search]); // Dependency on search changes

  const filteredProduct = (jewelleries || []).filter(
    (product: any) => !product.isDiscounted
  );
  const jewelProd = jewelleries.filter(
    (product: any) =>
      Array.isArray(discounts) &&
      discounts.some((discount: any) => discount._id === product?.discount?._id)
  );

  const displayedProducts = showAllProducts
    ? [...(jewelProd || []), ...filteredProduct]
    : selectedProducts || [];

  useEffect(() => {
    const payload = {
      filterOptions,
      page: 1,
      pageSize: 10,
    };
    // validation.resetForm();
    dispatch(onGetJewelleryDiscountModel(payload));
  }, [dispatch]);

  const debouncedSearch = useCallback(
    debounce((searchValue: string) => {
      const payload = {
        filterOptions: { search: searchValue }, // Only pass search term to payload
        page: 1,
        pageSize: 10,
      };
      dispatch(onGetJewelleryDiscountModel(payload)); // Dispatch action
    }, 1000),
    [dispatch] // Ensure `isEdit` and `dispatch` are dependencies
  );

  const handleJewellerySearchChange = (e: any) => {
    const searchValue = e.target.value;
    setFilterOptions((prevState) => ({
      ...prevState,
      search: searchValue,
    }));
  };

  useEffect(() => {
    if (filterOptions.search !== undefined) {
      debouncedSearch(filterOptions?.search); // Trigger debounced search on search change
    }
  }, [filterOptions?.search, debouncedSearch]);

  return (
    <div>
      <form
        action="#!"
        onSubmit={(e: any) => {
          e.preventDefault();
          validation.handleSubmit(); // Ensure this does not block execution
          return false;
        }}
      >
        <div>
          <div className="text-16 font-semibold pb-4 pt-6 border-t dark:border-zink-500">
            Products
          </div>
          <div className="flex justify-between">
            <div className="lg:col-span-3 w-1/3">
              <div className="relative">
                <input
                  type="text"
                  className="ltr:pl-8 rtl:pr-8 search form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                  placeholder="Search for ..."
                  autoComplete="off"
                  value={filterOptions.search || ""}
                  onChange={(e) => handleJewellerySearchChange(e)}
                />
                <Search className="inline-block size-4 absolute ltr:left-2.5 rtl:right-2.5 top-2.5 text-slate-500 dark:text-zink-200 fill-slate-100 dark:fill-zink-600" />
              </div>
            </div>
            {isEdit && (
              <button
                type="button"
                data-action="next"
                className={`text-white ${
                  isLoading ? "cursor-not-allowed opacity-50" : ""
                } btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20`}
                onClick={toggleEditMode}
                disabled={isLoading}
              >
                <span className="align-middle">{"Edit"}</span>{" "}
              </button>
            )}
          </div>
          <div
            className="mt-5 lg:max-h-[calc(theme('height.screen')_-_435px)] md:max-h-[calc(theme('height.screen')_-_530px)] max-h-[calc(theme('height.screen')_-_500px)] overflow-y-auto"
            id="scrollableDiv"
          >
            <InfiniteScroll
              dataLength={jewelleries.length} // Length of current jewelleries array
              next={() => {
                loadMoreJewelleries(currentPage + 1);
              }}
              hasMore={jewelleries?.length < totalCount} // Check if there are more items to load
              loader={<div>{isEdit ? "" : "Loading..."}</div>} // Loading spinner or message
              endMessage={<div>No more products</div>} // Message when all products are loaded
              scrollThreshold={0.1} // Trigger loading when 90% of the list is visible
              scrollableTarget="scrollableDiv" // The scrollable container, usually the div containing the list
            >
              <div
                className={`grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 gap-x-5`}
                id="cardGridView"
              >
                {(isEdit ? displayedProducts : filteredProduct || []).map(
                  (item: any, key: number) => {
                    const isSelected = selectedProducts.some(
                      (product: any) => product._id === item._id
                    );

                    return (
                      <div
                        className={`card cursor-pointer md:group-[.gridView]:flex relative\
                            ${isSelected ? "border-blue-500 border-2" : ""}
                            `}
                        key={key}
                        onClick={() => {
                          handleProductClick(item);
                        }}
                      >
                        <div className="relative group-[.gridView]:static px-5 pt-5 pb-6 group-[.gridView]:p-5">
                          <div className="group-[.gridView]:p-3 group-[.gridView]:bg-slate-100 dark:group-[.gridView]:bg-zink-600 group-[.gridView]:inline-block rounded-md">
                            <img
                              src={item.media[0]?.images?.[0]}
                              alt=""
                              className="group-[.gridView]:h-16"
                            />
                          </div>
                        </div>
                        <div className="card-body !pt-0 md:group-[.gridView]:flex group-[.gridView]:!p-5 group-[.gridView]:gap-3 group-[.gridView]:grow">
                          <div className="group-[.gridView]:grow">
                            <h6 className="mb-1 truncate transition-all duration-200 ease-linear text-15">
                              {item.title}
                            </h6>
                            <h5 className="mt-4 text-16">
                              {item.price || "$ 500"}{" "}
                              <small className="font-normal line-through text-slate-500 dark:text-zink-200">
                                {item.delPrice || "$ 800"}
                              </small>
                            </h5>
                          </div>
                        </div>
                      </div>
                    );
                  }
                )}
              </div>
            </InfiniteScroll>
            {isEdit && (
              <div>
                <button
                  type="button"
                  className={`
                    text-white btn bg-custom-600 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20
                    ${
                      !showUpdateButtons ? "cursor-not-allowed opacity-50" : ""
                    } 
                  `}
                  onClick={() => setShowAllProducts(true)}
                  disabled={!showUpdateButtons || isLoading}
                  // disabled={isDisabled}
                >
                  Load More
                </button>
              </div>
            )}
          </div>
        </div>
        <div className="bottom-0 bg-white p-4 pb-0 border-t dark:bg-zink-600 dark:border-zink-500">
          <div className="flex justify-between gap-2">
            <button
              type="button"
              data-action="prev"
              className={`duration-200 flex ${
                isLoading ? "cursor-not-allowed opacity-50" : ""
              } ease-linear mrtransition-all text-slate-500 btn bg-slate-200 border-slate-200 hover:text-slate-600 hover:bg-slate-300 hover:border-slate-300 focus:text-slate-600 focus:bg-slate-300 focus:border-slate-300 focus:ring focus:ring-slate-100 active:text-slate-600 active:bg-slate-300 active:border-slate-300 active:ring active:ring-slate-100`}
              onClick={() => changeTab("discountInfo")}
              disabled={isLoading}
            >
              {" "}
              <MoveLeft className="inline-block h-4 mr-1" />
              <span className="align-middle">Previous</span>
            </button>

            {isEdit && (
              <div className="flex justify-between gap-2">
                <button
                  type="button"
                  className={`text-white h-fit ${
                    isLoading ? "cursor-not-allowed opacity-50" : ""
                  } btn bg-gray-500 hover:bg-gray-600`}
                  onClick={() => {
                    setShowUpdateButtons(false);
                    validation.resetForm();
                    dispatch(resetSelectedProducts());
                    setIsFormChanged(false);
                    // setShowAllProducts(false);
                    toggle();
                  }}
                  disabled={isLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={`text-white btn bg-custom-500 hover:bg-custom-600 h-fit
                      ${
                        isLoading || !isFormChanged || !isButtonEnabled
                          ? "cursor-not-allowed opacity-50"
                          : ""
                      }
                      `}
                  disabled={isLoading || !isFormChanged || !isButtonEnabled}
                >
                  <div className="whitespace-nowrap">
                    {isLoading ? (
                      <div className="flex align-center">
                        <Loader2 className="size-4 ltr:mr-2 rtl:ml-2 animate-spin" />
                        Saving...
                      </div>
                    ) : (
                      "Save Changes"
                    )}
                  </div>
                </button>
              </div>
            )}
            {!isEdit && (
              <button
                type="submit"
                data-action="next"
                className={`text-white ${
                  isLoading ? "cursor-not-allowed opacity-50" : ""
                } btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20`}
                disabled={isLoading}
              >
                <span className="align-middle">
                  {isLoading ? (
                    <div className="flex align-center">
                      <Loader2 className="size-4 ltr:mr-2 rtl:ml-2 animate-spin" />
                      Submitting...
                    </div>
                  ) : (
                    "Submit"
                  )}
                </span>{" "}
              </button>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};

export default DiscountJewelleryProduct;
